import { UzInput } from '@utazzo/ui';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';

import type { CreateWebsiteDestinationPageRequest } from '@utazzo/shared';

import { useCategoryPagesList } from '@/features/category-pages/use-category-pages';

export const CategoryPagesMultiSelect = ({
  form,
  disabled,
}: {
  form: UseFormReturn<CreateWebsiteDestinationPageRequest>;
  disabled?: boolean;
}): React.JSX.Element => {
  const [search, setSearch] = useState('');
  const selectedIds = useWatch({ control: form.control, name: 'categoryPageIds' }) ?? [];

  const pagesQuery = useCategoryPagesList({
    page: 1,
    pageSize: 200,
    sortBy: 'title',
    sortDir: 'asc',
  });

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (pagesQuery.data?.data ?? [])
      .filter((p) => !term || p.title.toLowerCase().includes(term) || p.slug.includes(term))
      .map((p) => ({ id: p.id, label: p.title, slug: p.slug }));
  }, [pagesQuery.data, search]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const setSelected = (next: Set<string>): void => {
    form.setValue('categoryPageIds', Array.from(next), { shouldDirty: true });
  };

  const toggle = (id: string): void => {
    if (disabled) return;
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleAll = (): void => {
    if (disabled) return;
    const allVisible = rows.every((r) => selectedSet.has(r.id));
    const next = new Set(selectedSet);
    if (allVisible) {
      for (const r of rows) next.delete(r.id);
    } else {
      for (const r of rows) next.add(r.id);
    }
    setSelected(next);
  };

  return (
    <div className="space-y-2 lg:col-span-2">
      <div className="space-y-1">
        <label className="text-sm font-medium text-zinc-700">Category pages</label>
        <p className="text-xs text-zinc-500">
          Select which category rollups (e.g. India, International) this destination belongs to.
        </p>
      </div>
      <div className="relative">
        <Search className="absolute top-2.5 left-2 h-3.5 w-3.5 text-zinc-400" />
        <UzInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search category pages…"
          className="h-9 pl-8 text-sm"
          disabled={disabled || pagesQuery.isLoading}
        />
      </div>
      <div className="max-h-52 overflow-y-auto rounded-md border border-zinc-200 bg-zinc-50 p-2">
        {pagesQuery.isLoading ? (
          <p className="py-4 text-center text-xs text-zinc-500">Loading category pages…</p>
        ) : rows.length === 0 ? (
          <p className="py-4 text-center text-xs text-zinc-500">No category pages found.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {rows.map((row) => (
              <li key={row.id}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-white">
                  <input
                    type="checkbox"
                    checked={selectedSet.has(row.id)}
                    disabled={disabled}
                    onChange={() => toggle(row.id)}
                  />
                  <span className="truncate">
                    {row.label}
                    <span className="ml-1 text-xs text-zinc-400">/{row.slug}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        {rows.length > 0 && (
          <div className="mt-2 flex items-center justify-between border-t border-zinc-200 pt-2">
            <label className="flex items-center gap-2 text-xs text-zinc-500">
              <input
                type="checkbox"
                checked={rows.length > 0 && rows.every((r) => selectedSet.has(r.id))}
                disabled={disabled}
                onChange={toggleAll}
              />
              Select all visible
            </label>
            <span className="text-xs text-zinc-500">{selectedSet.size} selected</span>
          </div>
        )}
      </div>
    </div>
  );
};
